import { read, write } from './http';
import { MOCK_CONTESTS } from './mock/contests.mock';
import { MOCK_CONTEST_TEAM } from './mock/gameplay.mock';
import { demoEnteredContestId, demoTeam, rememberEntry } from './mock/demo-state';
import type { Contest, ContestEntry, ContestTeam } from '@/types';

/**
 * Contest discovery and entry.
 *
 * Entering a contest and picking an eleven are one request on the API, so
 * there is no separate "join" call — `enterContest` is the join.
 */

/**
 * GET /api/v1/contests
 *
 * Everything currently on offer. Past contests are excluded by default; the
 * list is not filtered to contests you can still enter, so callers decide how
 * to present a full or closed one.
 */
export function listContests(
  includePast = false,
  signal?: AbortSignal,
): Promise<readonly Contest[]> {
  return read(
    `/contests?includePast=${includePast}`,
    () => (includePast ? MOCK_CONTESTS : MOCK_CONTESTS.filter((c) => c.status === 'open')),
    signal,
  );
}

/** GET /api/v1/contests/:contestId */
export function getContest(contestId: string, signal?: AbortSignal): Promise<Contest> {
  return read(
    `/contests/${contestId}`,
    () => MOCK_CONTESTS.find((c) => c.id === contestId) ?? (MOCK_CONTESTS[0] as Contest),
    signal,
  );
}

/** GET /api/v1/me/contests — the ones already entered. */
export function getMyContests(
  includePast = false,
  signal?: AbortSignal,
): Promise<readonly Contest[]> {
  return read(
    `/me/contests?includePast=${includePast}`,
    () => {
      const entered = demoEnteredContestId();
      return entered ? MOCK_CONTESTS.filter((contest) => contest.id === entered) : [];
    },
    signal,
  );
}

/**
 * POST /api/v1/contests/:contestId/teams
 *
 * Enter a contest with a starting eleven and a bench of four. The API
 * validates the squad itself — sizes, position cover in the eleven, captain and
 * vice among the starters — and refuses a contest that is closed, at
 * capacity, or already entered, so those failures surface as messages rather
 * than being pre-empted here.
 *
 * `teamName` may be omitted, in which case the manager's saved team name is
 * used; the API rejects the entry if neither exists.
 */
export function enterContest(contestId: string, entry: ContestEntry): Promise<ContestTeam> {
  return write(`/contests/${contestId}/teams`, 'POST', entry, () =>
    rememberEntry(contestId, entry),
  );
}

/** GET /api/v1/contests/:contestId/my-team — full detail, same shape as /me/teams/:id/current. */
export function getMyTeamInContest(
  contestId: string,
  signal?: AbortSignal,
): Promise<ContestTeam> {
  return read(
    `/contests/${contestId}/my-team`,
    () => demoTeam() ?? MOCK_CONTEST_TEAM,
    signal,
  );
}
